import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';
import { useFlag as useUnleashFlag } from '@simplywallst/unleash';
import { Text, Heading, useMount, Grid, Button } from '@simplywallst/ui-core';

import { PortfolioIllustration } from '../../../../components/PortfolioIllustration';
import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { ROUTE_PORTFOLIO_TRANSACTIONS_EDIT } from '@/constants/routes';

import PortfolioLinkStatus from '../../../../components/PortfolioLinkStatus';
import { PortfolioSettings } from '../PortfolioSettings';

import { Section, TextWrapper } from './styled';

import { InvalidHoldingsBanner } from '../InvalidHoldingsBanner';

type Props = {
  portfolio: Portfolio;
};

export const EmptyTransactionsState = ({ portfolio }: Props) => {
  const dispatch = useDispatch();

  const invalidHoldingsflag = useUnleashFlag(
    'mono-portfolio-transactions-invalid-holdings'
  );
  const hasAccessToInvalidHoldings =
    invalidHoldingsflag.ready && invalidHoldingsflag.enabled;

  const showInvalidHoldingsBanner =
    hasAccessToInvalidHoldings && portfolio.invalidHoldings.length > 0;

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'EmptyTransactionsState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  return (
    <Track>
      {showInvalidHoldingsBanner && (
        <InvalidHoldingsBanner
          portfolioId={portfolio.id}
          invalidHoldings={portfolio.invalidHoldings}
          initialModalState={showInvalidHoldingsBanner}
        />
      )}
      <PortfolioPageHeader portfolio={portfolio}>
        <PortfolioSettings portfolio={portfolio} />
      </PortfolioPageHeader>
      <PortfolioLinkStatus portfolio={portfolio} />
      <Section
        as="section"
        templateRows="auto auto"
        placeItems="center"
        gap="16px"
      >
        <PortfolioIllustration />
        <TextWrapper>
          <Heading
            as="h3"
            typography="smallStrong"
            textAlign="center"
            data-cy-id="empty-state"
          >
            No open positions in your portfolio
          </Heading>
          <Text typography="xSmall" textAlign="center" color="mediumGray">
            Add transactions to track performance, important changes and analyze
            your portfolio quality and diversification
          </Text>
        </TextWrapper>
        <Grid justifyContent="center">
          <Button
            styleType="louderLarge"
            onClick={() => {
              dispatch({
                type: ROUTE_PORTFOLIO_TRANSACTIONS_EDIT,
                payload: { portfolioId: portfolio.id },
              });
            }}
          >
            Add transactions
          </Button>
        </Grid>
      </Section>
    </Track>
  );
};
